/**
 * RecordBetaResultModal
 *
 * Value-first capture of a transfer-bearing cycle's beta hCG result after the
 * two-week wait (#1210). The numeric value is the single source of truth: the
 * pregnancy outcome is *derived* from it, never self-reported by the patient.
 *
 * Flow (single capture step):
 *  1. "record"       — the beta hCG value (mIU/mL) is required. It can be typed
 *                      manually OR read from an uploaded result document by OCR
 *                      canonical processing run → the hCG marker in `results`. A
 *                      document alone is not enough — parsing must yield a value,
 *                      otherwise we ask the patient to add it manually.
 *  2. "confirmation" — empathetic per-outcome close-out derived from the value.
 *
 * Outcome derivation (standard clinical thresholds for a single beta hCG):
 *  - value < 5    → Not pregnant (no_pregnancy)
 *  - value >= 25  → Pregnant (pregnancy)
 *  - 5..25        → Borderline; a detectable-but-low beta. We record it as an
 *                   uncertain result that stays open for a repeat result and
 *                   never present it as a confirmed viable pregnancy.
 *
 * Parse states (a focused single-file subset of the shared document-upload
 * interaction model, PR #2015): idle → reading → parsed | unreadable.
 *
 * Persistence:
 *  - Attached documents are uploaded for real via the shared document hooks.
 *  - Non-FET cycles write the numeric value to the existing typed patient
 *    test-result contract before creating the canonical outcome event. FET
 *    cycles write to the patient-owned typed beta series instead, preserving
 *    repeatable result observations without diagnosing or closing the cycle.
 *
 * Skinned onto the shared moon-ui Dialog + design tokens (DM Sans, EMBER).
 */

import { useState, useCallback, useEffect, type ReactNode } from "react";
import { View, ScrollView, Image, useWindowDimensions } from "react-native";
import {
  Dialog,
  PrimaryButton,
  DialogHeader,
  DialogIcon,
  DialogActions,
  SecondaryButton,
  MoonFormField,
  MoonNumericField,
  DateField,
  MoonTimeField,
  DocumentRow,
  Row,
  DialogBodyText,
  MetaText,
  IconButton,
  ActionBanner,
  Tag,
  TextActionButton,
  TextField,
} from "@louise/moon-ui";
import {
  Calendar as CalendarIcon,
  Stethoscope,
  FileText,
  Droplet,
  PencilLine,
  Check,
  RefreshCw,
  RotateCcw,
  FolderPlus,
  X,
} from "lucide-react-native";
import { ThinkingOrbStatus } from "@src/components/ui/ThinkingOrbStatus";
import * as DocumentPicker from "expo-document-picker";
import {
  DocumentFileDropZone,
  type SelectedFile,
} from "@src/packages/document-vault/DocumentFileDropZone";
import { EMBER, semanticColors } from "@louise/moon-ui/theme/tokens";
import type {
  BetaResultType,
  CycleOutcomeResultStatus,
  PregnancyOutcomeKind,
  MiscarriageOutcomeKind,
} from "@src/packages/cycles/hooks/useCycles";
import {
  projectDocumentProcessingRunToPrefill,
  UPLOAD_FAILURE_PRESENTATION,
  useDocumentSourceTransfer,
  type DocumentProcessingPrefillResult,
} from "@src/packages/documents/ingestion";
import { toLocalDateString } from "@src/packages/utils/date";
import { useTranslations } from "@src/packages/i18n";
import { NegativeCloseoutSupport } from "./NegativeCloseoutSupport";
import {
  PregnancyNextSteps,
  pregnancyNextStepsSubtitleKey,
} from "./PregnancyNextSteps";

const TEST_ILLUSTRATION =
  require("@src/apps/moon/assets/images/you-icons/test-tubes.png") as number;

// Shared 3D file-type icons from the document-upload interaction model (#2015),
// so an attached result reads the same here as it does in the document vault.
const UPLOAD_FILE_ICONS = {
  document:
    require("@src/apps/moon/assets/images/upload-file-icons/document-3d.png") as number,
  image:
    require("@src/apps/moon/assets/images/upload-file-icons/image-3d.png") as number,
  generic:
    require("@src/apps/moon/assets/images/upload-file-icons/generic-3d.png") as number,
} as const;

// ── Outcome derivation ──────────────────────────────────────
// Standard clinical thresholds for a single beta hCG (mIU/mL). Derived from the
// value alone — the patient never self-declares the outcome.
const BETA_NEGATIVE_MAX = 5; // < 5   → treated as negative
const BETA_POSITIVE_MIN = 25; // >= 25 → treated as positive

type DerivedOutcome = "no_pregnancy" | "pregnancy" | "borderline" | null;

function deriveOutcome(value: number | null): DerivedOutcome {
  if (value === null) return null;
  if (value < BETA_NEGATIVE_MAX) return "no_pregnancy";
  if (value >= BETA_POSITIVE_MIN) return "pregnancy";
  return "borderline";
}

// ── OCR value extraction ────────────────────────────────────
// Pull the beta hCG number out of the extracted lab results. The blood-test
// schema returns a `results` array of { marker, value, unit }; we find the hCG
// marker (β-hCG, b-hCG, beta hCG, hCG, chorionic gonadotropin, …) and parse the
// first numeric token from its value.

function parseNumericValue(raw: string | null | undefined): number | null {
  if (!raw) return null;
  const match = raw.replace(/\s/g, "").match(/-?\d[\d.,]*/);
  if (!match) return null;
  let cleaned = match[0];
  if (cleaned.includes(",") && !cleaned.includes(".")) {
    const parts = cleaned.split(",");
    // A single trailing group of 1-2 digits reads as a decimal comma (fr);
    // anything else is a thousands separator.
    const decimalTail = parts.length === 2 ? parts[1] : undefined;
    cleaned =
      decimalTail !== undefined && decimalTail.length <= 2
        ? `${parts[0]}.${decimalTail}`
        : cleaned.replace(/,/g, "");
  } else {
    cleaned = cleaned.replace(/,/g, "");
  }
  const n = Number.parseFloat(cleaned);
  return Number.isNaN(n) ? null : n;
}

function findBetaValue(
  results: DocumentProcessingPrefillResult[] | null,
): number | null {
  if (!results) return null;
  for (const row of results) {
    const marker = (row.marker ?? "").toLowerCase().replace(/[^a-z]/g, "");
    if (marker.includes("hcg") || marker.includes("chorionic")) {
      const parsed = parseNumericValue(row.value);
      if (parsed !== null) return parsed;
    }
  }
  return null;
}

// ── Step machine ────────────────────────────────────────────

type Step =
  | "record" // value (typed or OCR-parsed) + document(s) + notes
  | "confirmation"; // empathetic post-submit screen

// Parse lifecycle for the uploaded result document. "processing" covers the
// upload + OCR read; the attached-file row's ThinkingOrbStatus splits it into
// an "Uploading… → Reading…" label using the OCR hook's uploadId signal.
type ParseState = "idle" | "processing" | "parsed" | "unreadable";

// ── Local file model (mirrors AddSpecimenModal's attach pattern) ──
type AttachedFile = {
  name: string;
  uri: string;
  mimeType?: string;
  /** Set once the file has been uploaded so retries don't re-upload it. */
  uploadId?: string;
  /** Set once the uploaded file has been filed in the document vault. */
  documentId?: string;
  uploadError?: boolean;
};

// ── Props ───────────────────────────────────────────────────

export type BetaResultPayload = {
  mode: "cycle_outcome" | "fet_beta_order";
  type: BetaResultType | null;
  resultStatus: CycleOutcomeResultStatus;
  pregnancyOutcome?: PregnancyOutcomeKind | null;
  miscarriageOutcome?: MiscarriageOutcomeKind | null;
  biochemicalPregnancyObserved?: boolean | null;
  notes: string;
  betaHcgValue?: number | null;
  collectionAt?: string | null;
  collectionTimeUnknown?: boolean;
  timeZone?: string;
  sourceDocumentId?: string | null;
  /** The transfer day she entered here, when none was on record. YYYY-MM-DD. */
  transferDate?: string;
};

type Props = {
  visible: boolean;
  /** Dismiss without finalising (X / backdrop / Cancel). No navigation. */
  onClose: () => void;
  /** Write the outcome. Resolves on success, throws on failure. The modal
   *  transitions to its confirmation step on success and stays open. */
  onConfirm: (payload: BetaResultPayload) => Promise<void>;
  /** User read the confirmation content and tapped Done — parent should
   *  dismiss + navigate (typically to Care home). Falls back to onClose. */
  onComplete?: () => void;
  /** Transfer (or insemination) date — used by the positive confirmation
   *  step to estimate the first ultrasound window (~6 weeks post-transfer).
   *  YYYY-MM-DD. Optional; the screen falls back to a generic message when
   *  unavailable. */
  transferDate?: string | null;
  isLoading: boolean;
  error: string | null;
  /** Viewer perspective — "partner" reframes a few headline strings for the
   *  AMAB partner of an AFAB primary patient. */
  perspective?: "self" | "partner";
  /** Partner's first name for the partner-perspective copy. */
  partnerDisplayName?: string;
  /** FET results stay source-linked to the typed beta series; they are not
   * turned into a patient-facing pregnancy diagnosis by this modal. */
  isFETBetaOrder?: boolean;
  /**
   * No transfer is on record for this FET cycle, and a beta result belongs to a
   * transfer. Ask for the day here, prefilled from `transferDate`, instead of
   * failing the save and sending her somewhere else to add it.
   */
  needsTransferDate?: boolean;
  /** Optional: navigate the reader to a suggested article. When provided, the
   * negative-outcome confirmation surfaces a small reading rail. */
  onOpenArticle?: (articleId: string) => void;
};

// ── Component ───────────────────────────────────────────────

export function RecordBetaResultModal({
  visible,
  onClose,
  onConfirm,
  onComplete,
  transferDate,
  isLoading,
  error,
  perspective = "self",
  partnerDisplayName,
  isFETBetaOrder = false,
  needsTransferDate = false,
  onOpenArticle,
}: Props) {
  const t = useTranslations();
  const { height } = useWindowDimensions();

  const [step, setStep] = useState<Step>("record");
  const [value, setValue] = useState<number | null>(null);
  const [collectionDate, setCollectionDate] = useState("");
  const [collectionTime, setCollectionTime] = useState("");
  const [enteredTransferDate, setEnteredTransferDate] = useState(
    transferDate ?? "",
  );
  // The plan's transfer day can arrive after the modal mounts; take it as the
  // prefill unless she has already typed one.
  useEffect(() => {
    if (!visible || !transferDate) return;
    setEnteredTransferDate((current) => current || transferDate);
  }, [visible, transferDate]);
  const [files, setFiles] = useState<AttachedFile[]>([]);
  const [notes, setNotes] = useState("");
  const [parseState, setParseState] = useState<ParseState>("idle");
  const [uploading, setUploading] = useState(false);
  // The derived outcome the user submitted, so the confirmation step can show
  // the right empathetic content (positive / negative / borderline).
  const [confirmationOutcome, setConfirmationOutcome] =
    useState<DerivedOutcome>(null);

  // Document upload hooks — self-contained (they resolve tenant/patient via
  // useApiClient), so wiring them here needs no changes to the parent.
  const transfer = useDocumentSourceTransfer();

  const busy = isLoading || uploading;
  const isProcessing = parseState === "processing";
  // A source remains available even when extraction is unreadable, so Save can
  // file the original upload without sending the file to S3 again.
  const hasUploaded = files.some((file) => Boolean(file.uploadId));

  const reset = useCallback(() => {
    setStep("record");
    setValue(null);
    setCollectionDate("");
    setCollectionTime("");
    setEnteredTransferDate(transferDate ?? "");
    setFiles([]);
    setNotes("");
    setParseState("idle");
    setUploading(false);
    setConfirmationOutcome(null);
    transfer.reset();
  }, [transfer, transferDate]);

  const handleClose = useCallback(() => {
    if (!busy) {
      reset();
      onClose();
    }
  }, [busy, onClose, reset]);

  const handleDone = useCallback(() => {
    reset();
    if (onComplete) {
      onComplete();
    } else {
      onClose();
    }
  }, [reset, onComplete, onClose]);

  // Typing a value clears a lingering "could not read" status, since the
  // patient has now supplied the value manually.
  const handleValueChange = useCallback((next: number | null) => {
    setValue(next);
    if (next !== null) {
      setParseState((prev) => (prev === "unreadable" ? "idle" : prev));
    }
  }, []);

  // ── OCR parse of an uploaded result document ──
  const runOcrForValue = useCallback(
    async (file: {
      name: string;
      uri: string;
      mimeType?: string;
      uploadId?: string;
    }) => {
      // Upload the source once, then run OCR against that durable upload. The
      // source identity is retained when extraction is unreadable so the
      // patient can still save the original report or retry reading it.
      if (!file.uploadId) transfer.reset();
      setParseState("processing");
      try {
        const source = file.uploadId
          ? { file, uploadId: file.uploadId }
          : await transfer.uploadSource(file, { destination: "lab_result" });
        setFiles((current) =>
          current.map((currentFile) =>
            currentFile.uri === file.uri
              ? { ...currentFile, uploadId: source.uploadId }
              : currentFile,
          ),
        );
        const data = projectDocumentProcessingRunToPrefill(
          (
            await transfer.processSource(source, {
              attempts: 24,
              intervalMs: 1_250,
            })
          ).run,
          file.name,
        );
        const parsed = findBetaValue(data.results ?? null);
        if (parsed !== null) {
          // Don't clobber a value the patient typed while we were reading.
          setValue((prev) => prev ?? parsed);
          setParseState("parsed");
        } else {
          setParseState("unreadable");
        }
      } catch {
        // Extraction failed (or no tenant/patient) — ask for manual entry.
        setParseState("unreadable");
      }
    },
    [transfer],
  );

  // Attach the picked file(s) and, when we still need a value, OCR the first
  // one to try to read the beta hCG automatically.
  const attachAndParse = useCallback(
    (incoming: { name: string; uri: string; mimeType?: string }[]) => {
      if (incoming.length === 0) return;
      setFiles((prev) => [
        ...prev,
        ...incoming.map((f) => ({
          name: f.name,
          uri: f.uri,
          mimeType: f.mimeType,
        })),
      ]);
      if (value !== null || isProcessing) return;
      const target = incoming[0];
      if (!target) return;
      void runOcrForValue(target);
    },
    [value, isProcessing, runOcrForValue],
  );

  const handlePickFile = useCallback(async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ["application/pdf", "image/*"],
        copyToCacheDirectory: true,
        multiple: true,
      });
      if (!result.canceled && result.assets && result.assets.length > 0) {
        attachAndParse(
          result.assets.map((asset) => ({
            name: asset.name,
            uri: asset.uri,
            mimeType: asset.mimeType ?? undefined,
          })),
        );
      }
    } catch {
      // silently ignore — the drop zone re-offers on next tap
    }
  }, [attachAndParse]);

  const handleFilesSelected = useCallback(
    (selected: SelectedFile[]) => {
      attachAndParse(
        selected.map((file) => ({
          name: file.name,
          uri: file.uri,
          mimeType: file.mimeType,
        })),
      );
    },
    [attachAndParse],
  );

  const handleRemoveFile = useCallback((idx: number) => {
    setFiles((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      if (next.length === 0) setParseState("idle");
      return next;
    });
  }, []);

  // Upload attached result document(s) for REAL, into the patient's document
  // vault. Best-effort + per-file non-blocking, exactly like AddSpecimenModal.
  const uploadAttachedDocuments = useCallback(async (): Promise<
    string | null
  > => {
    let firstDocumentId =
      files.find((file) => file.documentId)?.documentId ?? null;
    if (!files.some((file) => !file.documentId)) return firstDocumentId;
    setUploading(true);
    try {
      const today = toLocalDateString(new Date());
      for (const file of files) {
        if (file.documentId) continue;
        try {
          let uploadId = file.uploadId;
          if (!uploadId) {
            const source = await transfer.uploadSource(
              {
                uri: file.uri,
                name: file.name,
                mimeType: file.mimeType,
              },
              { destination: "lab_result" },
            );
            // Guard against re-upload if outcome recording fails and retries.
            uploadId = source.uploadId;
            file.uploadId = uploadId;
          }
          if (!file.documentId) {
            const documentId = await transfer.fileSource(
              {
                file: {
                  uri: file.uri,
                  name: file.name,
                  mimeType: file.mimeType,
                },
                uploadId,
              },
              {
                name: `${t("care.beta_result.document_name_prefix")}: ${file.name}`,
                documentDate: today,
                tags: ["beta-hcg"],
              },
            );
            file.documentId = documentId;
          }
          if (file.documentId) {
            firstDocumentId ??= file.documentId;
          }
          setFiles((current) =>
            current.map((currentFile) =>
              currentFile.uri === file.uri
                ? { ...currentFile, uploadError: false }
                : currentFile,
            ),
          );
        } catch {
          // non-blocking — one failed file shouldn't block recording the outcome
          setFiles((current) =>
            current.map((currentFile) =>
              currentFile.uri === file.uri
                ? { ...currentFile, uploadError: true }
                : currentFile,
            ),
          );
        }
      }
    } finally {
      setUploading(false);
    }
    return firstDocumentId;
  }, [files, t, transfer]);

  const handleSave = useCallback(async () => {
    // A value is required — either typed or read from a document.
    if (value === null || busy) return;
    if (needsTransferDate && !enteredTransferDate) return;

    // Persist attached documents for real first, so they land in the vault
    // even if outcome recording fails and the user retries.
    const sourceDocumentId = await uploadAttachedDocuments();

    // Keep the readable value line for the outcome ledger. The parent also
    // persists the numeric value through the typed test-result endpoint.
    const trimmed = notes.trim();
    const valueLine = `${t("care.beta_result.value_notes_prefix")}: ${value} ${t(
      "care.beta_result.value_unit",
    )}`;
    const composedNotes = [valueLine, trimmed].filter(Boolean).join("\n");

    const derived = deriveOutcome(value); // non-null (value is set)
    const collectionAt =
      isFETBetaOrder && collectionDate
        ? collectionTime
          ? new Date(`${collectionDate}T${collectionTime}:00`).toISOString()
          : collectionDate
        : null;
    const timeZone =
      isFETBetaOrder && collectionDate
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : undefined;

    // The mode says which records the value lands in, not what it means. An FET
    // beta additionally lands in the FET beta ledger, but it is read the same way
    // and it ends the wait the same way — the two-week-wait panel gives way to the
    // recorded-result panel on `cycleOutcome.type`, so a value that wrote no
    // outcome left an FET cycle showing a countdown and a "pick a test date"
    // prompt with a result already in hand.
    const mode = isFETBetaOrder ? "fet_beta_order" : "cycle_outcome";
    const collection = isFETBetaOrder
      ? {
          collectionAt,
          collectionTimeUnknown: !collectionTime,
          timeZone,
          ...(needsTransferDate ? { transferDate: enteredTransferDate } : {}),
        }
      : {};

    try {
      if (derived === "no_pregnancy") {
        await onConfirm({
          mode,
          type: "no_pregnancy",
          resultStatus: "negative",
          biochemicalPregnancyObserved: null,
          notes: composedNotes,
          betaHcgValue: value,
          sourceDocumentId,
          ...collection,
        });
      } else if (derived === "borderline") {
        await onConfirm({
          mode,
          type: null,
          resultStatus: "uncertain",
          biochemicalPregnancyObserved: true,
          notes: composedNotes,
          betaHcgValue: value,
          sourceDocumentId,
          ...collection,
        });
      } else {
        // live_birth / multiple_live_birth is a post-delivery fact recorded
        // later via the past-cycle "Update outcome" affordance.
        await onConfirm({
          mode,
          type: "pregnancy",
          resultStatus: "positive",
          pregnancyOutcome: null,
          notes: composedNotes,
          betaHcgValue: value,
          sourceDocumentId,
          ...collection,
        });
      }
      setConfirmationOutcome(derived);
      // Only transition on success. onConfirm throws on API failure and the
      // parent surfaces the error via the `error` prop, so the user can fix
      // and retry without losing context.
      setStep("confirmation");
    } catch {
      // Stay on the current step; error banner will render from props.
    }
  }, [
    value,
    busy,
    uploadAttachedDocuments,
    notes,
    onConfirm,
    t,
    isFETBetaOrder,
    collectionDate,
    collectionTime,
    needsTransferDate,
    enteredTransferDate,
  ]);

  const isPartner = perspective === "partner";
  const headerTitle =
    step === "record"
      ? isPartner
        ? t("care.beta_result.header_partner")
        : t("care.beta_result.header_self")
      : confirmationOutcome === "pregnancy"
        ? t("care.beta_result.such_wonderful_news")
        : confirmationOutcome === "borderline"
          ? t("care.beta_result.header_borderline")
          : t("care.beta_result.header_with_you");

  const dialogMaxHeight = Math.min(760, Math.max(480, height * 0.85));

  // Centered header subtitle. A pregnancy carries its encouragement here, right
  // under the title; the other confirmations render their own body copy.
  const headerSubtitle =
    step === "record"
      ? isPartner
        ? t("care.beta_result.intro_partner")
        : t("care.beta_result.intro_self")
      : confirmationOutcome === "pregnancy"
        ? t(pregnancyNextStepsSubtitleKey(transferDate ?? null))
        : undefined;

  const canSave =
    value !== null && !busy && (!needsTransferDate || !!enteredTransferDate);

  return (
    <Dialog
      visible={visible}
      onClose={step === "confirmation" ? handleDone : handleClose}
      layout="centered"
      closeButton="minimal"
      testID="record-beta-result-modal"
      surfaceStyle={{ maxHeight: dialogMaxHeight }}
      contentStyle={{
        paddingTop: 20,
        paddingHorizontal: 22,
        paddingBottom: 18,
        maxHeight: dialogMaxHeight,
      }}
    >
      <View style={{ flex: 1, minHeight: 0 }}>
        {/* Centered header */}
        <DialogIcon
          source={TEST_ILLUSTRATION}
          width={56}
          height={62}
          style={{ marginBottom: 16 }}
        />
        <DialogHeader
          align="center"
          title={headerTitle}
          subtitle={headerSubtitle}
          style={{ width: "100%", marginBottom: 16 }}
          subtitleStyle={{ textAlign: "center", width: "100%" }}
        />

        {/* Body */}
        <ScrollView
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          style={{ flexShrink: 1 }}
          contentContainerStyle={{ paddingBottom: 4 }}
        >
          {step === "record" ? (
            <RecordStep
              value={value}
              setValue={handleValueChange}
              files={files}
              onPick={handlePickFile}
              onFilesSelected={handleFilesSelected}
              onRemove={handleRemoveFile}
              onRetry={() => {
                const firstFile = files[0];
                if (firstFile) void runOcrForValue(firstFile);
              }}
              onRetryUpload={() => void uploadAttachedDocuments()}
              parseState={parseState}
              hasUploaded={hasUploaded}
              notes={notes}
              setNotes={setNotes}
              isFET={isFETBetaOrder}
              needsTransferDate={needsTransferDate}
              transferDate={enteredTransferDate}
              setTransferDate={setEnteredTransferDate}
              collectionDate={collectionDate}
              setCollectionDate={setCollectionDate}
              collectionTime={collectionTime}
              setCollectionTime={setCollectionTime}
              isLoading={busy}
              perspective={perspective}
              partnerDisplayName={partnerDisplayName}
            />
          ) : (
            <ConfirmationStep
              outcome={confirmationOutcome}
              value={value}
              transferDate={transferDate}
              onOpenArticle={onOpenArticle}
            />
          )}
        </ScrollView>

        {/* Error */}
        {error && (
          <ActionBanner
            variant="important"
            message={error}
            style={{ marginTop: 12 }}
          />
        )}

        {/* Footer */}
        {step === "record" ? (
          <DialogActions style={{ marginTop: 14 }}>
            {/* No Cancel — the dialog's close button already dismisses. The
                secondary action is the persistent add-document control. */}
            <SecondaryButton
              label={t("care.beta_result.documents_drop_action")}
              leading={
                <FolderPlus
                  size={17}
                  color={semanticColors.text.secondary}
                  strokeWidth={1.8}
                />
              }
              onPress={() => void handlePickFile()}
              disabled={busy}
              flex={1}
              testID="beta-add-document"
            />
            <PrimaryButton
              label={t("care.beta_result.save")}
              onPress={() => void handleSave()}
              loading={busy}
              disabled={!canSave}
              flex={1}
            />
          </DialogActions>
        ) : (
          <PrimaryButton
            label={t("care.beta_result.done")}
            onPress={handleDone}
            style={{ marginTop: 14 }}
          />
        )}
      </View>
    </Dialog>
  );
}

// ── Record step ─────────────────────────────────────────────
// Single capture step. The beta hCG value is required — the patient types it,
// or uploads a result document we OCR-parse to read it automatically.

function RecordStep({
  value,
  setValue,
  files,
  onPick,
  onFilesSelected,
  onRemove,
  onRetry,
  onRetryUpload,
  parseState,
  hasUploaded,
  isFET,
  needsTransferDate,
  transferDate,
  setTransferDate,
  collectionDate,
  setCollectionDate,
  collectionTime,
  setCollectionTime,
  notes,
  setNotes,
  isLoading,
  perspective = "self",
  partnerDisplayName,
}: {
  value: number | null;
  setValue: (v: number | null) => void;
  files: AttachedFile[];
  onPick: () => void;
  onFilesSelected: (files: SelectedFile[]) => void;
  onRemove: (index: number) => void;
  onRetry: () => void;
  onRetryUpload: () => void;
  parseState: ParseState;
  hasUploaded: boolean;
  isFET: boolean;
  needsTransferDate: boolean;
  transferDate: string;
  setTransferDate: (date: string) => void;
  collectionDate: string;
  setCollectionDate: (value: string) => void;
  collectionTime: string;
  setCollectionTime: (value: string) => void;
  notes: string;
  setNotes: (s: string) => void;
  isLoading: boolean;
  perspective?: "self" | "partner";
  partnerDisplayName?: string;
}) {
  const t = useTranslations();
  const isPartner = perspective === "partner";
  const valueLabel =
    isPartner && partnerDisplayName
      ? t("care.beta_result.value_label_partner", {
          params: { name: partnerDisplayName },
        })
      : t("care.beta_result.value_label_self");

  // Document upload sits ABOVE the value input: it is a helper that fills the
  // field in place. The value input stays the primary field and is always
  // visible + editable, even while a document is uploading or being read.
  return (
    <View style={{ gap: 20 }}>
      <DocumentsField
        files={files}
        onPick={onPick}
        onFilesSelected={onFilesSelected}
        onRemove={onRemove}
        onRetry={onRetry}
        onRetryUpload={onRetryUpload}
        parseState={parseState}
        hasUploaded={hasUploaded}
        disabled={isLoading}
      />

      <MoonNumericField
        label={valueLabel}
        labelIcon={Droplet}
        value={value}
        onChange={setValue}
        placeholder={t("care.beta_result.value_placeholder")}
        suffix={t("care.beta_result.value_unit")}
        min={0}
        disabled={isLoading}
        textInputProps={{ accessibilityLabel: valueLabel }}
        testID="beta-hcg-value-input"
      />

      {needsTransferDate ? (
        <DateField
          label={t("care.beta_result.transfer_date")}
          labelIcon={CalendarIcon}
          hint={t("care.beta_result.result_is_saved_against_transfer")}
          value={transferDate}
          onChange={setTransferDate}
          disabled={isLoading}
          testID="beta-transfer-date-input"
        />
      ) : null}

      {isFET ? (
        <View style={{ gap: 12 }}>
          <DateField
            label={t("care.beta_result.collection_date_label")}
            labelIcon={CalendarIcon}
            hint={t("care.beta_result.collection_date_hint")}
            value={collectionDate}
            onChange={setCollectionDate}
            disabled={isLoading}
            testID="beta-collection-date-input"
          />
          <MoonTimeField
            label={t("care.beta_result.collection_time_label")}
            value={collectionTime}
            onChange={setCollectionTime}
            disabled={isLoading}
            testID="beta-collection-time-input"
          />
        </View>
      ) : null}

      <NotesField
        notes={notes}
        setNotes={setNotes}
        isLoading={isLoading}
        placeholder={t("care.beta_result.notes_placeholder_default")}
      />
    </View>
  );
}

// ── Documents attach + OCR parse status ─────────────────────
// Each attached file is a canonical DocumentRow whose trailing indicator speaks
// the shared document-ingestion state language (#2015): an animated
// ThinkingOrbStatus while uploading/reading, then a quiet terminal marker once
// the value is read (Check) or could not be read (AlertCircle). No bespoke
// colored status banners — the row IS the status surface.

function DocumentsField({
  files,
  onPick,
  onFilesSelected,
  onRemove,
  onRetry,
  onRetryUpload,
  parseState,
  hasUploaded,
  disabled,
}: {
  files: AttachedFile[];
  onPick: () => void;
  onFilesSelected: (files: SelectedFile[]) => void;
  onRemove: (index: number) => void;
  onRetry: () => void;
  onRetryUpload: () => void;
  parseState: ParseState;
  hasUploaded: boolean;
  disabled: boolean;
}) {
  const t = useTranslations();
  const hasFiles = files.length > 0;
  // The "Result document" section is always present. With no document yet, the
  // upload surface (drop zone) sits over the value input. Once a document is
  // attached that surface is removed and the file row(s) take its place —
  // adding another result is done from the footer's add-document icon button.
  return (
    <MoonFormField
      label={t("care.beta_result.documents_label")}
      labelIcon={FileText}
    >
      {hasFiles ? (
        <View style={{ gap: 6 }}>
          {files.map((file, idx) => (
            <AttachedFileRow
              key={`${file.name}-${idx}`}
              file={file}
              parseState={idx === 0 ? parseState : "idle"}
              hasUploaded={hasUploaded}
              onRemove={() => onRemove(idx)}
              onRetry={() => {
                if (file.uploadError) {
                  onRetryUpload();
                } else {
                  onRetry();
                }
              }}
              disabled={disabled}
            />
          ))}
        </View>
      ) : (
        <DocumentFileDropZone
          onPick={onPick}
          onFilesSelected={onFilesSelected}
          compact
          primaryLabel={t("care.beta_result.documents_drop_action")}
          secondaryLabel={t("care.beta_result.documents_drop_hint")}
        />
      )}
    </MoonFormField>
  );
}

function FileTypeIcon({ fileName }: { fileName: string }) {
  const extension = fileName.split(".").pop()?.toLowerCase();
  const kind = extension
    ? ["jpg", "jpeg", "png", "gif", "webp", "heic"].includes(extension)
      ? "image"
      : ["pdf", "doc", "docx", "txt", "csv", "xls", "xlsx"].includes(extension)
        ? "document"
        : "generic"
    : "generic";
  return (
    <Image
      accessibilityLabel={`${extension ?? "generic"} file`}
      source={UPLOAD_FILE_ICONS[kind]}
      resizeMode="contain"
      style={{ height: 34, width: 34 }}
    />
  );
}

// One attached document rendered in the shared ingestion state language. Active
// upload/read shows the thinking orb (with its built-in cancel); terminal states
// show a quiet marker plus a remove control, and the guidance lives in the row
// subtitle rather than a colored banner.
function AttachedFileRow({
  file,
  parseState,
  hasUploaded,
  onRemove,
  onRetry,
  disabled,
}: {
  file: AttachedFile;
  parseState: ParseState;
  hasUploaded: boolean;
  onRemove: () => void;
  onRetry: () => void;
  disabled: boolean;
}) {
  const t = useTranslations();
  const active = parseState === "processing";

  // A failure reads like the Documents list: the status under the name and a
  // red retry beside it, labelled for the step that failed.
  const failure = file.uploadError
    ? UPLOAD_FAILURE_PRESENTATION.upload_failed
    : parseState === "unreadable"
      ? UPLOAD_FAILURE_PRESENTATION.extraction_failed
      : null;
  const subtitle = failure
    ? t(failure.statusKey)
    : parseState === "parsed"
      ? t("care.beta_result.parsed")
      : undefined;

  let trailing: ReactNode;
  if (active) {
    trailing = (
      <ThinkingOrbStatus
        state={hasUploaded ? "shaping" : "composing"}
        label={
          hasUploaded
            ? t("care.beta_result.reading")
            : t("care.beta_result.uploading")
        }
        size={20}
        onCancel={disabled ? undefined : onRemove}
      />
    );
  } else {
    trailing = (
      <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
        {parseState === "parsed" ? (
          <Tag
            label={t("care.beta_result.parsed_badge")}
            icon={Check}
            variant="outline"
            shape="pill"
          />
        ) : failure ? (
          <TextActionButton
            label={t(failure.actionKey)}
            action={failure.actionKey}
            onPress={onRetry}
            disabled={disabled}
            tone="destructive"
            size="xs"
            icon={RotateCcw}
            style={{ paddingHorizontal: 4 }}
          />
        ) : null}
        <IconButton
          accessibilityLabel={t("care.beta_result.remove_attached_file", {
            params: { name: file.name },
          })}
          action="remove_beta_attachment"
          onPress={onRemove}
          disabled={disabled}
          size="xs"
          hitSlop={8}
        >
          <X size={16} color={EMBER.colors.textFaint} />
        </IconButton>
      </View>
    );
  }

  return (
    <DocumentRow
      leading={<FileTypeIcon fileName={file.name} />}
      title={file.name}
      subtitle={subtitle}
      trailing={trailing}
      showChevron={false}
      titleNumberOfLines={1}
      subtitleNumberOfLines={2}
      style={{ paddingVertical: 8 }}
    />
  );
}

// ── Shared inputs ───────────────────────────────────────────

function NotesField({
  notes,
  setNotes,
  isLoading,
  placeholder,
}: {
  notes: string;
  setNotes: (s: string) => void;
  isLoading: boolean;
  placeholder?: string;
}) {
  const t = useTranslations();
  return (
    <TextField
      label={t("care.beta_result.notes_label")}
      labelIcon={PencilLine}
      value={notes}
      onChangeText={setNotes}
      placeholder={
        placeholder ?? t("care.beta_result.notes_placeholder_default")
      }
      numberOfLines={3}
      editable={!isLoading}
      notes
    />
  );
}

// ── ConfirmationStep ───────────────────────────────────────
// Per-outcome empathetic close-out derived from the value: positive (timeline +
// practical next steps), borderline (repeat recommended), or negative (gentle,
// ready-when-you-are).

function ConfirmationStep({
  outcome,
  value,
  transferDate,
  onOpenArticle,
}: {
  outcome: DerivedOutcome;
  value: number | null;
  transferDate?: string | null;
  onOpenArticle?: (articleId: string) => void;
}) {
  return (
    <View style={{ gap: 18 }}>
      {outcome === "pregnancy" ? (
        <PregnancyNextSteps transferDate={transferDate ?? null} />
      ) : outcome === "borderline" ? (
        <BorderlineBody value={value} />
      ) : (
        <NegativeCloseoutSupport onOpenArticle={onOpenArticle} />
      )}
    </View>
  );
}

// ── Borderline (repeat beta recommended) ───────────────────
// A detectable-but-low beta (5..25). We never present it as a confirmed viable
// pregnancy — instead we explain that a repeat draw is what tells us more.

function BorderlineBody({ value }: { value: number | null }) {
  const t = useTranslations();
  const subhead =
    value !== null
      ? t("care.beta_result.borderline_subhead", {
          params: { value: String(value) },
        })
      : t("care.beta_result.borderline_subhead", { params: { value: "—" } });

  return (
    <>
      <DialogBodyText>{subhead}</DialogBodyText>

      <View style={{ gap: 10 }}>
        <MetaText style={{ marginTop: 0 }}>
          {t("care.beta_result.whats_next")}
        </MetaText>
        <Row
          leading={<RefreshCw size={18} color={EMBER.colors.warm} />}
          title={t("care.beta_result.borderline_repeat_label")}
          subtitle={t("care.beta_result.borderline_repeat_hint")}
        />
        <Row
          leading={<Stethoscope size={18} color={EMBER.colors.warm} />}
          title={t("care.beta_result.resource_care_team_label")}
          subtitle={t("care.beta_result.resource_care_team_hint")}
        />
      </View>

      <MetaText style={{ textAlign: "center" }}>
        {t("care.beta_result.borderline_note")}
      </MetaText>
    </>
  );
}
